/// Variables that the backend's `render_template` recognizes for each
/// task type, plus the data we need to render the catalog and inline
/// preview in the Edit Alert Rule modal.
///
/// Keep in sync with the backend:
///   - `services/notification_dispatcher/mod.rs::dispatch_actions`
///     (task_id, task_name, device_id, device_name, current_value)
///   - `workers/task_scheduler.rs::evaluate_metric_alert_task`
///     (threshold, metric_name, window_minutes via config_vars)
export interface TemplateVar {
  name: string;
  exampleValue: string;
  description: string;
}

// metric_alert tasks have the richest variable set — dispatch-time vars
// (device_*, task_*, current_value) plus config_vars (threshold,
// metric_name, window_minutes) built before dispatch.
const METRIC_ALERT_VARS: TemplateVar[] = [
  { name: 'task_id', exampleValue: '8f2c…3a1b', description: 'UUID of the alert rule (this task).' },
  { name: 'task_name', exampleValue: 'Interface utilization > 90%', description: 'Display name of the alert rule.' },
  { name: 'device_id', exampleValue: 'a6b6…5660', description: 'UUID of the device the alert is firing for.' },
  { name: 'device_name', exampleValue: 'core-switch-01', description: 'Display name of the affected device.' },
  {
    name: 'current_value',
    exampleValue: '92.5',
    description:
      'Reading at the moment the alert last evaluated. Avoid in descriptions — the incident is rewritten every evaluation, so a value here fills its timeline with edits. The incident charts the metric instead.',
  },
  { name: 'threshold', exampleValue: '90.0', description: 'Threshold from the condition (threshold/threshold_ratio).' },
  { name: 'metric_name', exampleValue: 'interface_in_octets', description: 'The metric being evaluated.' },
  { name: 'window_minutes', exampleValue: '10', description: 'How far back the rule reads, in minutes.' },
  {
    name: 'alerting_series',
    exampleValue: 'ge-0/0/3, ge-0/0/7',
    description: 'Which ports (or processors) met the rule, by name: at most five, then how many more.',
  },
];

// device_audit tasks are evaluated entirely by per-audit Rust code,
// which writes incident title/description/dedupe_key directly. The
// template fields in the action JSON are kept around for symmetry but
// are not actually rendered for the title/description. The
// dedupe_payload variable does appear in some audit dedupe-key
// fragments (e.g. `task:{task_id}:region:{dedupe_payload}`).
const DEVICE_AUDIT_VARS: TemplateVar[] = [
  { name: 'task_id', exampleValue: '8f2c…3a1b', description: 'UUID of the audit task.' },
  { name: 'task_name', exampleValue: 'VLAN naming consistency', description: 'Display name of the audit.' },
  { name: 'device_id', exampleValue: 'a6b6…5660', description: 'UUID of the device (when the finding is per-device).' },
  { name: 'device_name', exampleValue: 'core-switch-01', description: 'Display name of the device.' },
  { name: 'dedupe_payload', exampleValue: 'vlan-1', description: 'Per-finding key fragment (e.g. VLAN id, MAC) used in dedupe keys.' },
];

export function getTemplateVars(taskType: string): TemplateVar[] {
  switch (taskType) {
    case 'metric_alert':
      return METRIC_ALERT_VARS;
    case 'device_audit':
      return DEVICE_AUDIT_VARS;
    default:
      return [];
  }
}

/// Render a `{var}`-style template against a {var → value} map. Mirrors
/// the backend's `render_template` (sequential string replace).
/// Unknown variables are left as `{var}` in the output, matching the
/// backend's missing-var behavior.
export function renderTemplate(template: string, vars: Record<string, string>): string {
  let out = template;
  for (const [k, v] of Object.entries(vars)) {
    out = out.split(`{${k}}`).join(v);
  }
  return out;
}

export function exampleVarMap(taskType: string): Record<string, string> {
  const map: Record<string, string> = {};
  for (const v of getTemplateVars(taskType)) {
    map[v.name] = v.exampleValue;
  }
  return map;
}
